import { useContext, useEffect, useRef, useState } from "react";
import translations from "../../../translations/translations";
import "../adminModals.css";
import "./AdminServiceCategoriesModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { Plus, Trash2 } from "lucide-react";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import useModalDismiss from "../../common/useModalDismiss";

const AdminServiceCategoriesModal = ({
	isOpen,
	onClose,
	shopId,
	initialCategories = [],
	services = [],
	language = "en",
	onSave,
	/* true when this opens on top of the services editor, so its scrim
	   doesn't double-dim what's already dimmed behind it */
	stacked = false,
}) => {
	useModalDismiss(onClose, { enabled: isOpen });
	const { userData, setUserData} = useContext(CombinedContext);

	const [categories, setCategories] = useState([]);
	const [saving, setSaving] = useState(false);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);


	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	// Stable fingerprint of the categories the draft was last built from.
	const categoriesSignature = (list) =>
		JSON.stringify(
			(list || []).map((c) => [
				c.categoryId ?? null,
				typeof c.name === "string" ? c.name : (c.name?.en ?? ""),
				typeof c.name === "string" ? c.name : (c.name?.es ?? ""),
				Number(c.order) || 0,
				!!c.isDefault,
			]),
		);

	const hydratedFrom = useRef(null);

	useEffect(() => {
		if (!isOpen) {
			hydratedFrom.current = null;
			return;
		}

		// The parent refetches the whole shop whenever anything is saved, handing us a
		// new initialCategories array holding identical data. Re-copying on identity
		// alone would discard the edits the user has not saved yet.
		const signature = categoriesSignature(initialCategories);
		if (hydratedFrom.current === signature) return;
		hydratedFrom.current = signature;

		// deep copy so we don't mutate parent state
		setCategories(JSON.parse(JSON.stringify(initialCategories)));
	}, [isOpen, initialCategories]);

	if (!isOpen) return null;

	const updateName = (id, value) => {
		setCategories((prev) =>
			prev.map((c) =>
				c.categoryId === id
					? {
							...c,
							name: {
								en: value,
								es: value,
							},
					  }
					: c
			)
		);
	};

	const addCategory = () => {
		setCategories((prev) => {
			const nextOrder =
				Math.max(0, ...prev.map((c) => Number(c.order) || 0)) + 1;

			return [
				...prev,
				{
					categoryId: `cat_${crypto.randomUUID()}`,
					name: {
						en: "New category",
						es: "Nueva categoría",
					},
					order: nextOrder,
				},
			];
		});
	};

	const deleteCategory = (categoryId) => {
		const hasServices = services.some(
			(s) => s.categoryId === categoryId
		);

		if (hasServices) {
			showBanner(
				"error",
				translations[language].profile.categories.cannotDeleteWithServices
			);
			return;
		}

		setCategories((prev) => prev.filter((c) => c.categoryId !== categoryId));
	};

	const handleSave = async () => {
		if (!shopId) {
			showBanner("error", translations[language].profile.categories.missingShop);
			return;
		}

		try {
			setSaving(true);

			// Optional: normalize order
			const normalized = categories.map((c, idx) => ({
				...c,
				order: idx + 1,
			}));

			const token = userData?.cognitoTokens?.AccessToken;

			const res = await companyBasedRequest.updateShopServiceCategories(
				shopId,
				normalized,
				token
			);

			const updatedCategories =
				res?.shop?.serviceCategories ||
				res?.serviceCategories ||
				normalized;

			onSave?.(updatedCategories);
            setUserData((prev) => ({
                ...prev,
                shop: {
                    ...prev.shop,
                    serviceCategories: res.serviceCategories,
                },
            }));


			onClose();
		} catch (err) {
			console.error("Failed to save categories:", err);
			showBanner("error", translations[language].profile.categories.saveError);
		} finally {
			setSaving(false);
		}
	};

	const t = translations[language].profile;
	const catT = t.modal.categories;

	return (
		<div className={`sf-pm-overlay${stacked ? " is-stacked" : ""}`}>
			<div className="sf-pm-modal is-compact">
				{/* Header */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{catT.title}</h2>
						<p className="sf-pm-sub">{catT.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} disabled={saving} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* Body */}
				<div className="sf-pm-body">
					<section className="sf-pm-section">
						<div className="sf-pm-label-row">
							<p className="sf-pm-label">{t.categories.categories}</p>
							{categories.length > 0 && <span className="sf-pm-count">{categories.length}</span>}
						</div>

						{categories.length === 0 ? (
							<p className="sf-pm-empty">{catT.empty}</p>
						) : (
							<div className="sf-pm-cat-list">
								{categories.map((cat, idx) => (
									<div key={cat.categoryId} className="sf-pm-cat-row">
										<span className="sf-pm-cat-order" aria-hidden="true">
											{idx + 1}
										</span>

										<input
											className="sf-pm-input"
											value={
												typeof cat.name === "string"
													? cat.name
													: cat.name?.[language] || cat.name?.en || ""
											}
											onChange={(e) =>
												updateName(cat.categoryId, e.target.value)
											}
											disabled={saving}
											aria-label={`${catT.title} ${idx + 1}`}
										/>

										{!cat.isDefault && (
											<button
												type="button"
												className="sf-pm-iconbtn is-danger"
												onClick={() => deleteCategory(cat.categoryId)}
												disabled={saving}
												aria-label="Delete category"
											>
												<Trash2 size={17} strokeWidth={1.8} />
											</button>
										)}
									</div>
								))}
							</div>
						)}

						<button
							type="button"
							className="sf-pm-add"
							onClick={addCategory}
							disabled={saving}
						>
							<Plus size={16} strokeWidth={2.2} />
							{t.categories.addCategory}
						</button>
					</section>
				</div>

				{/* Footer */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default AdminServiceCategoriesModal;
